// FLAGS: personal-data
// The event-creation form. Holds the chosen employer and the experts assigned to each session —
// names and identities of OpenUp staff — and is the first Spaces surface to send employer data.
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import { ArrowLeft, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@openup/shared/primitives/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@openup/shared/primitives/ui/card";
import { Input } from "@openup/shared/primitives/ui/input";
import MarkdownDescriptionEditor from "@src/components/spaces/MarkdownDescriptionEditor";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import FormField from "@src/components/spaces/FormField";
import CompanyPicker from "@src/components/spaces/CompanyPicker";
import SessionCard from "@src/components/spaces/SessionCard";
import EventTranslations from "@src/components/spaces/EventTranslations";
import {
  EventFormState,
  FormErrors,
  SessionFormState,
  emptyForm,
  emptySession,
  hasErrors,
  toRequest,
  validateForm,
  withCompanies,
} from "@src/components/spaces/createEventForm";
import {
  useCreateEventMutation,
  useEmployers,
  useHostableExperts,
} from "@src/queries/events";
import { SpacesApiError } from "@src/services/spacesError";
import { SpaceEventType } from "@src/models/spaces";

/** Route slug → event type. Unknown slugs bounce back to the type picker. */
const EVENT_TYPE_BY_SLUG: Record<string, SpaceEventType> = {
  "group-session": "groupsession",
  masterclass: "masterclass",
};

const TYPE_LABEL: Record<SpaceEventType, string> = {
  groupsession: "group session",
  masterclass: "masterclass",
};

const EventNewPage = () => {
  const navigate = useNavigate();
  const { eventType: slug = "" } = useParams();
  const eventType = EVENT_TYPE_BY_SLUG[slug];
  const employersQuery = useEmployers();
  const expertsQuery = useHostableExperts();
  // Internal tool: a console error is the whole treatment for now. Worth knowing what the scheduler
  // sees when this fires — with neither list the employer select holds only its placeholder and no
  // host can be assigned, so the form is unsubmittable while validation blames them ("Pick a
  // company.", "Assign at least one host."). Surfacing it properly is deferred, not overlooked.
  useEffect(() => {
    if (employersQuery.isError) {
      console.error("Spaces: failed to load employers", employersQuery.error);
    }
  }, [employersQuery.isError, employersQuery.error]);

  useEffect(() => {
    if (expertsQuery.isError) {
      console.error(
        "Spaces: failed to load hostable experts",
        expertsQuery.error,
      );
    }
  }, [expertsQuery.isError, expertsQuery.error]);
  const createEvent = useCreateEventMutation();

  const [form, setForm] = useState<EventFormState>(() =>
    emptyForm(eventType ?? "masterclass"),
  );
  const [errors, setErrors] = useState<FormErrors>({ sessions: {} });

  // Unknown/missing type slug — send the scheduler back to pick one.
  if (!eventType) {
    return <Navigate to="/events/new" replace />;
  }

  const patch = (partial: Partial<EventFormState>) =>
    setForm((prev) => ({ ...prev, ...partial }));

  const updateSession = (index: number, next: SessionFormState) =>
    setForm((prev) => ({
      ...prev,
      sessions: prev.sessions.map((s, i) => (i === index ? next : s)),
    }));

  const addSession = () =>
    setForm((prev) => ({
      ...prev,
      sessions: [...prev.sessions, emptySession()],
    }));

  const removeSession = (index: number) =>
    setForm((prev) => ({
      ...prev,
      sessions: prev.sessions.filter((_, i) => i !== index),
    }));

  const onSubmit = () => {
    const nextErrors = validateForm(form);
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    createEvent.mutate(toRequest(form), {
      onSuccess: (event) => {
        toast.success(`${TYPE_LABEL[eventType]} created. Now invite people.`);
        navigate(`/events/${event.id}/invitees`);
      },
      onError: (error) => {
        const detail =
          error instanceof SpacesApiError
            ? error.detail
            : {
                code: "VALIDATION" as const,
                message: "Could not create the event.",
              };
        toast.error(detail.message);
        if (detail.sessionIndex !== undefined) {
          setErrors((prev) => ({
            ...prev,
            sessions: {
              ...prev.sessions,
              [detail.sessionIndex as number]: {
                ...prev.sessions[detail.sessionIndex as number],
                experts: detail.message,
              },
            },
          }));
        }
      },
    });
  };

  const experts = expertsQuery.data ?? [];

  return (
    <div className="mx-auto max-w-3xl">
      <Link
        to="/events/new"
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Choose a different type
      </Link>
      <h1 className="mb-6 text-2xl font-semibold text-indigo-800">
        Create a {TYPE_LABEL[eventType]}
      </h1>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <FormField label="Title" required error={errors.title}>
            {({ id, invalid }) => (
              <Input
                id={id}
                value={form.title}
                aria-invalid={invalid}
                placeholder="e.g. Managing stress at work"
                onChange={(e) => patch({ title: e.target.value })}
              />
            )}
          </FormField>

          <FormField label="Companies" required error={errors.employerIds}>
            {({ id, invalid }) => (
              <>
                <CompanyPicker
                  id={id}
                  invalid={invalid}
                  employers={employersQuery.data ?? []}
                  selectedIds={form.employerIds}
                  disabled={employersQuery.isLoading}
                  onChange={(employerIds) =>
                    patch(
                      withCompanies(
                        form,
                        employersQuery.data ?? [],
                        employerIds,
                      ),
                    )
                  }
                />
                <p className="text-sm text-gray-600">
                  Employees of any company listed here can register. Add every
                  entity that should be able to attend.
                </p>
              </>
            )}
          </FormField>

          <FormField
            label="Name shown on the public page"
            required={form.employerIds.length > 1}
            error={errors.employerDisplayName}
          >
            {({ id }) => (
              <>
                <Input
                  id={id}
                  value={form.employerDisplayName}
                  placeholder="e.g. Rabobank"
                  onChange={(e) =>
                    patch({ employerDisplayName: e.target.value })
                  }
                />
                <p className="text-sm text-gray-600">
                  Attendees see &quot;Live session for [name] employees&quot;.
                  {form.employerIds.length > 1
                    ? " Required here, because no single company name describes the audience."
                    : " Leave it blank to use the company name."}
                </p>
              </>
            )}
          </FormField>

          <FormField label="Description">
            {() => (
              <MarkdownDescriptionEditor
                value={form.description}
                onChange={(markdown) => patch({ description: markdown })}
              />
            )}
          </FormField>

          {eventType === "masterclass" && (
            <FormField
              label="Post-session survey link"
              error={errors.surveyUrl}
            >
              {({ id, invalid }) => (
                <>
                  <Input
                    id={id}
                    type="url"
                    value={form.surveyUrl}
                    aria-invalid={invalid}
                    placeholder="https://forms.example.com/…"
                    onChange={(e) => patch({ surveyUrl: e.target.value })}
                  />
                  <p className="text-sm text-gray-600">
                    Attendees are sent to this third-party survey when a session
                    ends. Leave blank for no survey.
                  </p>
                </>
              )}
            </FormField>
          )}
        </CardContent>
      </Card>

      <div className="mt-6">
        <EventTranslations
          sourceLanguage={form.languageCode}
          title={form.title}
          description={form.description}
          sessions={form.sessions.map((session, index) => ({
            key: session.key,
            label: `Session ${index + 1}`,
            // A blank name means the session inherits the event title, so the
            // event title is what there is to translate.
            sourceName: session.name.trim() || form.title,
            sourceInstructions: session.sessionInstructions,
          }))}
          translations={form.translations}
          languageError={errors.languageCode}
          onSourceLanguageChange={(languageCode) => patch({ languageCode })}
          onTranslationsChange={(translations) => patch({ translations })}
        />
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-indigo-800">Sessions</h2>
          <Button variant="secondary" size="sm" onClick={addSession}>
            <Plus className="mr-1 h-4 w-4" aria-hidden />
            Add session
          </Button>
        </div>

        {expertsQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          form.sessions.map((session, index) => (
            <SessionCard
              key={session.key}
              index={index}
              session={session}
              eventType={eventType}
              errors={errors.sessions[index]}
              experts={experts}
              canRemove={form.sessions.length > 1}
              onChange={(next) => updateSession(index, next)}
              onRemove={() => removeSession(index)}
            />
          ))
        )}
      </div>

      <div className="mt-8 flex justify-end gap-3">
        <Button variant="ghost" onClick={() => navigate("/")}>
          Cancel
        </Button>
        <Button
          variant="default"
          onClick={onSubmit}
          disabled={createEvent.isPending}
        >
          {createEvent.isPending && (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
          )}
          Create {TYPE_LABEL[eventType]}
        </Button>
      </div>
    </div>
  );
};

export default EventNewPage;
